import { apiFetch } from "./api";

// Klienti i "Punëve të mia". Ndarë nga api.ts sepse ai skedar është nën ndryshim paralel;
// këtu varet vetëm nga apiFetch, i cili është publik.

export type PersonalTaskPriority = "low" | "normal" | "high";
export type PersonalTaskStatus = "open" | "done";

export type PersonalTask = {
  id: string;
  user_id: string;
  school_id: string;
  title: string;
  description: string;
  category: string | null;
  priority: PersonalTaskPriority;
  due_at: string | null;
  status: PersonalTaskStatus;
  completed_at: string | null;
  series_id: string | null;
  recurrence_rule: string | null;
  created_at: string;
  updated_at: string;
};

export type PersonalTaskReminder = {
  id: string;
  task_id: string;
  due_at: string;
  kind: "once" | "repeat";
  repeat_every_minutes: number | null;
  repeats_remaining: number | null;
  sent_at: string | null;
  created_at: string;
};

export type PersonalTaskSubtask = {
  id: string;
  task_id: string;
  title: string;
  done: number;
  position: number;
  created_at: string;
};

export type PersonalTaskNote = {
  id: string;
  task_id: string;
  body: string;
  created_at: string;
  updated_at: string;
};

export type PersonalTasksResponse = {
  tasks: PersonalTask[];
  reminders: PersonalTaskReminder[];
  subtasks: PersonalTaskSubtask[];
  notes: PersonalTaskNote[];
};

const BASE = "/api/btec/personal-tasks";

function scoped(path: string, schoolId: string) {
  return `${path}?schoolId=${encodeURIComponent(schoolId)}`;
}

function taskPath(taskId: string, suffix = "") {
  return `${BASE}/${encodeURIComponent(taskId)}${suffix}`;
}

function send<T>(token: string | null, path: string, method: string, body?: unknown) {
  return apiFetch<T>(path, {
    token,
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function fetchPersonalTasks(token: string | null, schoolId: string) {
  return apiFetch<PersonalTasksResponse>(scoped(BASE, schoolId), { token });
}

export function createPersonalTask(
  token: string | null,
  schoolId: string,
  body: {
    title: string; description?: string; category?: string | null;
    priority?: PersonalTaskPriority; dueAt?: string | null;
  },
) {
  return send<{ task: PersonalTask }>(token, scoped(BASE, schoolId), "POST", body);
}

export function updatePersonalTask(
  token: string | null,
  schoolId: string,
  taskId: string,
  body: Partial<{
    title: string; description: string; category: string | null;
    priority: PersonalTaskPriority; dueAt: string | null; status: PersonalTaskStatus;
  }>,
) {
  return send<{ task: PersonalTask }>(token, scoped(taskPath(taskId), schoolId), "PATCH", body);
}

export function deletePersonalTask(token: string | null, schoolId: string, taskId: string) {
  return send<{ ok: true }>(token, scoped(taskPath(taskId), schoolId), "DELETE");
}

export function addPersonalTaskReminder(
  token: string | null,
  schoolId: string,
  taskId: string,
  body: {
    dueAt: string; kind: "once" | "repeat";
    repeatEveryMinutes?: number; repeatsRemaining?: number;
  },
) {
  return send<{ reminder: PersonalTaskReminder }>(
    token, scoped(taskPath(taskId, "/reminders"), schoolId), "POST", body,
  );
}

export function updatePersonalTaskReminder(
  token: string | null,
  schoolId: string,
  taskId: string,
  reminderId: string,
  body: { dueAt?: string; repeatsRemaining?: number },
) {
  return send<{ reminder: PersonalTaskReminder }>(
    token, scoped(taskPath(taskId, `/reminders/${encodeURIComponent(reminderId)}`), schoolId),
    "PATCH", body,
  );
}

export function deletePersonalTaskReminder(
  token: string | null, schoolId: string, taskId: string, reminderId: string,
) {
  return send<{ ok: true }>(
    token, scoped(taskPath(taskId, `/reminders/${encodeURIComponent(reminderId)}`), schoolId),
    "DELETE",
  );
}

export function addPersonalTaskSubtask(
  token: string | null, schoolId: string, taskId: string, body: { title: string; done?: boolean },
) {
  return send<{ subtask: PersonalTaskSubtask }>(
    token, scoped(taskPath(taskId, "/subtasks"), schoolId), "POST", body,
  );
}

export function updatePersonalTaskSubtask(
  token: string | null,
  schoolId: string,
  taskId: string,
  subtaskId: string,
  body: { title?: string; done?: boolean; position?: number },
) {
  return send<{ subtask: PersonalTaskSubtask }>(
    token, scoped(taskPath(taskId, `/subtasks/${encodeURIComponent(subtaskId)}`), schoolId),
    "PATCH", body,
  );
}

export function deletePersonalTaskSubtask(
  token: string | null, schoolId: string, taskId: string, subtaskId: string,
) {
  return send<{ ok: true }>(
    token, scoped(taskPath(taskId, `/subtasks/${encodeURIComponent(subtaskId)}`), schoolId),
    "DELETE",
  );
}

export function addPersonalTaskNote(
  token: string | null, schoolId: string, taskId: string, body: { body: string },
) {
  return send<{ note: PersonalTaskNote }>(
    token, scoped(taskPath(taskId, "/notes"), schoolId), "POST", body,
  );
}

export function updatePersonalTaskNote(
  token: string | null, schoolId: string, taskId: string, noteId: string, body: { body: string },
) {
  return send<{ note: PersonalTaskNote }>(
    token, scoped(taskPath(taskId, `/notes/${encodeURIComponent(noteId)}`), schoolId),
    "PATCH", body,
  );
}

export function deletePersonalTaskNote(
  token: string | null, schoolId: string, taskId: string, noteId: string,
) {
  return send<{ ok: true }>(
    token, scoped(taskPath(taskId, `/notes/${encodeURIComponent(noteId)}`), schoolId), "DELETE",
  );
}
